import { useState, useEffect, useCallback, useMemo } from 'react';
import API from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  FileText, DownloadSimple, ArrowClockwise, Funnel, Calendar,
  WhatsappLogo, Robot, Users, ChatTeardropText, ArrowDownLeft,
  ArrowUpRight, Check, X, FilePdf, FileCsv, CaretDown, ShieldCheck,
  Timer, Clock, Hourglass, Lightning, WarningCircle
} from '@phosphor-icons/react';

export default function ReportsPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  // Filters State
  const [adminId, setAdminId] = useState('all');
  const [datePreset, setDatePreset] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedWaIds, setSelectedWaIds] = useState([]);
  const [selectedBotIds, setSelectedBotIds] = useState([]);

  // Data State
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // overview, daily, whatsapp, bots, delay, recent

  // Delay Tab Filters State
  const [delayChannelFilter, setDelayChannelFilter] = useState('all'); // all, whatsapp, bot
  const [delaySeverityFilter, setDelaySeverityFilter] = useState('all'); // all, fast, moderate, delayed, critical
  const [delaySearch, setDelaySearch] = useState('');
  const [delaySubTab, setDelaySubTab] = useState('replies'); // replies, pending, staff

  // Popover open states
  const [waPopoverOpen, setWaPopoverOpen] = useState(false);
  const [botPopoverOpen, setBotPopoverOpen] = useState(false);
  const [waSearch, setWaSearch] = useState('');
  const [botSearch, setBotSearch] = useState('');

  // Fetch Report Data
  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        admin_id: adminId,
        date_preset: datePreset,
      };
      if (datePreset === 'custom') {
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      }
      if (selectedWaIds.length > 0) {
        params.whatsapp_api_ids = selectedWaIds.join(',');
      }
      if (selectedBotIds.length > 0) {
        params.bot_ids = selectedBotIds.join(',');
      }

      const res = await API.get('/reports/comprehensive', { params });
      setReportData(res.data);
    } catch (err) {
      console.error('Failed to load reports:', err);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [adminId, datePreset, startDate, endDate, selectedWaIds, selectedBotIds]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const filterOptions = reportData?.filter_options || { admins: [], bots: [], whatsapp_apis: [] };
  const metrics = reportData?.metrics;
  const metadata = reportData?.metadata;

  // Filtered dropdown lists for search
  const filteredWaList = useMemo(() => {
    const list = filterOptions.whatsapp_apis || [];
    if (!waSearch.trim()) return list;
    const q = waSearch.toLowerCase();
    return list.filter(w => (w.display_name || '').toLowerCase().includes(q) || (w.phone_number_id || '').includes(q));
  }, [filterOptions.whatsapp_apis, waSearch]);

  const filteredBotList = useMemo(() => {
    const list = filterOptions.bots || [];
    if (!botSearch.trim()) return list;
    const q = botSearch.toLowerCase();
    return list.filter(b => (b.bot_name || b.name || '').toLowerCase().includes(q) || (b.bot_username || b.username || '').toLowerCase().includes(q));
  }, [filterOptions.bots, botSearch]);

  const handleToggleWa = (id) => {
    setSelectedWaIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleToggleBot = (id) => {
    setSelectedBotIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSelectAllWa = () => {
    setSelectedWaIds((filterOptions.whatsapp_apis || []).map(a => a.id));
  };

  const handleClearWa = () => {
    setSelectedWaIds([]);
  };

  const handleSelectAllBots = () => {
    setSelectedBotIds((filterOptions.bots || []).map(b => b.id));
  };

  const handleClearBots = () => {
    setSelectedBotIds([]);
  };

  const resetAllFilters = () => {
    setAdminId('all');
    setDatePreset('today');
    setStartDate('');
    setEndDate('');
    setSelectedWaIds([]);
    setSelectedBotIds([]);
  };

  // Memoized filters for Response Delay logs
  const filteredDelayReplies = useMemo(() => {
    const list = reportData?.delayed_reports?.reply_logs || [];
    return list.filter(item => {
      if (delayChannelFilter !== 'all' && item.channel !== delayChannelFilter) return false;
      if (delaySeverityFilter !== 'all' && item.delay_severity !== delaySeverityFilter) return false;
      if (delaySearch.trim()) {
        const q = delaySearch.toLowerCase();
        const matchesClient = (item.client_name || '').toLowerCase().includes(q) || (item.client_id || '').toLowerCase().includes(q);
        const matchesStaff = (item.replied_by || '').toLowerCase().includes(q);
        const matchesText = (item.incoming_text || '').toLowerCase().includes(q) || (item.reply_text || '').toLowerCase().includes(q);
        if (!matchesClient && !matchesStaff && !matchesText) return false;
      }
      return true;
    });
  }, [reportData, delayChannelFilter, delaySeverityFilter, delaySearch]);

  const filteredDelayPending = useMemo(() => {
    const list = reportData?.delayed_reports?.pending_logs || [];
    return list.filter(item => {
      if (delayChannelFilter !== 'all' && item.channel !== delayChannelFilter) return false;
      if (delaySeverityFilter !== 'all' && item.waiting_severity !== delaySeverityFilter) return false;
      if (delaySearch.trim()) {
        const q = delaySearch.toLowerCase();
        const matchesClient = (item.client_name || '').toLowerCase().includes(q) || (item.client_id || '').toLowerCase().includes(q);
        const matchesText = (item.incoming_text || '').toLowerCase().includes(q);
        if (!matchesClient && !matchesText) return false;
      }
      return true;
    });
  }, [reportData, delayChannelFilter, delaySeverityFilter, delaySearch]);

  // CSV Export Handler
  const handleDownloadCsv = () => {
    if (!reportData) return;
    setExportingCsv(true);
    try {
      const { metadata, metrics, daily_breakdown, whatsapp_details, bot_details } = reportData;
      const lines = [];

      // Title & Metadata
      lines.push('"# COMPREHENSIVE ACTIVITY & PERFORMANCE REPORT"');
      lines.push(`"# Generated At","${metadata?.generated_at || new Date().toISOString()}"`);
      lines.push(`"# Filter Date Preset","${metadata?.date_preset || ''}"`);
      lines.push(`"# Date Range Label","${metadata?.range_label || ''}"`);
      lines.push(`"# Admin Filter","${adminId === 'all' ? 'All Admins' : adminId}"`);
      lines.push('');

      // KPI Summary Table
      lines.push('"=== EXECUTIVE METRICS SUMMARY ==="');
      lines.push('"Platform","Metric","Today","Yesterday","Selected Period","All Time"');
      
      // WhatsApp rows
      lines.push(`"WhatsApp","New Users / Contacts Joined","${metrics?.today?.whatsapp?.users_joined || 0}","${metrics?.yesterday?.whatsapp?.users_joined || 0}","${metrics?.period?.whatsapp?.users_joined || 0}","${metrics?.all_time?.whatsapp?.users_joined || 0}"`);
      lines.push(`"WhatsApp","Incoming Messages","${metrics?.today?.whatsapp?.incoming_msgs || 0}","${metrics?.yesterday?.whatsapp?.incoming_msgs || 0}","${metrics?.period?.whatsapp?.incoming_msgs || 0}","${metrics?.all_time?.whatsapp?.incoming_msgs || 0}"`);
      lines.push(`"WhatsApp","Outgoing Messages","${metrics?.today?.whatsapp?.outgoing_msgs || 0}","${metrics?.yesterday?.whatsapp?.outgoing_msgs || 0}","${metrics?.period?.whatsapp?.outgoing_msgs || 0}","${metrics?.all_time?.whatsapp?.outgoing_msgs || 0}"`);
      lines.push(`"WhatsApp","Total Messages","${metrics?.today?.whatsapp?.total_msgs || 0}","${metrics?.yesterday?.whatsapp?.total_msgs || 0}","${metrics?.period?.whatsapp?.total_msgs || 0}","${metrics?.all_time?.whatsapp?.total_msgs || 0}"`);

      // Telegram Bot rows
      lines.push(`"Telegram Bots","Users Joined","${metrics?.today?.bots?.users_joined || 0}","${metrics?.yesterday?.bots?.users_joined || 0}","${metrics?.period?.bots?.users_joined || 0}","${metrics?.all_time?.bots?.users_joined || 0}"`);
      lines.push(`"Telegram Bots","Incoming Messages","${metrics?.today?.bots?.incoming_msgs || 0}","${metrics?.yesterday?.bots?.incoming_msgs || 0}","${metrics?.period?.bots?.incoming_msgs || 0}","${metrics?.all_time?.bots?.incoming_msgs || 0}"`);
      lines.push(`"Telegram Bots","Outgoing Messages","${metrics?.today?.bots?.outgoing_msgs || 0}","${metrics?.yesterday?.bots?.outgoing_msgs || 0}","${metrics?.period?.bots?.outgoing_msgs || 0}","${metrics?.all_time?.bots?.outgoing_msgs || 0}"`);
      lines.push(`"Telegram Bots","Total Messages","${metrics?.today?.bots?.total_msgs || 0}","${metrics?.yesterday?.bots?.total_msgs || 0}","${metrics?.period?.bots?.total_msgs || 0}","${metrics?.all_time?.bots?.total_msgs || 0}"`);

      // Combined rows
      lines.push(`"COMBINED TOTAL","Total Users Joined","${metrics?.today?.combined?.users_joined || 0}","${metrics?.yesterday?.combined?.users_joined || 0}","${metrics?.period?.combined?.users_joined || 0}","${metrics?.all_time?.combined?.users_joined || 0}"`);
      lines.push(`"COMBINED TOTAL","Total Incoming Messages","${metrics?.today?.combined?.incoming_msgs || 0}","${metrics?.yesterday?.combined?.incoming_msgs || 0}","${metrics?.period?.combined?.incoming_msgs || 0}","${metrics?.all_time?.combined?.incoming_msgs || 0}"`);
      lines.push(`"COMBINED TOTAL","Total Outgoing Messages","${metrics?.today?.combined?.outgoing_msgs || 0}","${metrics?.yesterday?.combined?.outgoing_msgs || 0}","${metrics?.period?.combined?.outgoing_msgs || 0}","${metrics?.all_time?.combined?.outgoing_msgs || 0}"`);
      lines.push(`"COMBINED TOTAL","Grand Total Messages","${metrics?.today?.combined?.total_msgs || 0}","${metrics?.yesterday?.combined?.total_msgs || 0}","${metrics?.period?.combined?.total_msgs || 0}","${metrics?.all_time?.combined?.total_msgs || 0}"`);
      lines.push('');

      // Daily Breakdown Table
      lines.push('"=== DAILY BREAKDOWN TIMELINE ==="');
      lines.push('"Date","WA Users Joined","WA Incoming","WA Outgoing","WA Total","Bot Users Joined","Bot Incoming","Bot Outgoing","Bot Total","Total Users","Total Incoming","Total Outgoing","Grand Total Messages"');
      (daily_breakdown || []).forEach(row => {
        lines.push(`"${row.date}","${row.wa_users}","${row.wa_incoming}","${row.wa_outgoing}","${row.wa_total}","${row.bot_users}","${row.bot_incoming}","${row.bot_outgoing}","${row.bot_total}","${row.total_users}","${row.total_incoming}","${row.total_outgoing}","${row.total_messages}"`);
      });
      lines.push('');

      // WhatsApp Channels Breakdown
      lines.push('"=== WHATSAPP CHANNELS BREAKDOWN ==="');
      lines.push('"Channel Name","Phone Number ID","Period Users","Period Incoming","Period Outgoing","Period Total Messages","Today Users","All Time Users"');
      (whatsapp_details || []).forEach(wa => {
        lines.push(`"${wa.display_name}","${wa.phone_number_id}","${wa.period_users}","${wa.period_incoming}","${wa.period_outgoing}","${wa.period_total}","${wa.today_users}","${wa.all_time_users}"`);
      });
      lines.push('');

      // Telegram Bots Breakdown
      lines.push('"=== TELEGRAM BOTS BREAKDOWN ==="');
      lines.push('"Bot Name","Username","Period Users","Period Incoming","Period Outgoing","Period Total Messages","Today Users","All Time Users"');
      (bot_details || []).forEach(b => {
        lines.push(`"${b.name}","@${b.username}","${b.period_users}","${b.period_incoming}","${b.period_outgoing}","${b.period_total}","${b.today_users}","${b.all_time_users}"`);
      });
      lines.push('');

      // Response Delay / Reply Latency Report
      if (reportData?.delayed_reports) {
        const dSum = reportData.delayed_reports.summary || {};
        lines.push('"=== RESPONSE DELAY / LATENCY REPORT ==="');
        lines.push('"Metric","Value"');
        lines.push(`"Total Replies Analyzed","${dSum.total_replies || 0}"`);
        lines.push(`"Average Delay (Sending Msg - Incoming Msg)","${dSum.avg_delay_formatted || '0s'}"`);
        lines.push(`"Median Delay","${dSum.median_delay_formatted || '0s'}"`);
        lines.push(`"Max Delay","${dSum.max_delay_formatted || '0s'}"`);
        lines.push(`"Fast Replies (< 5m)","${dSum.fast_count || 0} (${dSum.fast_percent || 0}%)"`);
        lines.push(`"Moderate Delay (5m - 15m)","${dSum.moderate_count || 0} (${dSum.moderate_percent || 0}%)"`);
        lines.push(`"Delayed (15m - 1h)","${dSum.delayed_count || 0} (${dSum.delayed_percent || 0}%)"`);
        lines.push(`"Critical Delay (> 1h)","${dSum.critical_count || 0} (${dSum.critical_percent || 0}%)"`);
        lines.push(`"Pending Unanswered Clients","${dSum.pending_unanswered_count || 0}"`);
        lines.push(`"WhatsApp Average Delay","${dSum.whatsapp?.avg_delay_formatted || '0s'}"`);
        lines.push(`"Telegram Bot Average Delay","${dSum.bots?.avg_delay_formatted || '0s'}"`);
        lines.push('');
        lines.push('"=== REPLIED DELAY LOG (TOP RECORDS) ==="');
        lines.push('"Channel","Channel Name","Client Name","Client ID","Incoming Time","Reply Time","Delay Duration","Replied By","Staff Role"');
        (reportData.delayed_reports.reply_logs || []).slice(0, 100).forEach(r => {
          lines.push(`"${r.channel}","${r.channel_name}","${r.client_name}","${r.client_id}","${r.incoming_time}","${r.reply_time}","${r.delay_formatted}","${r.replied_by}","${r.replied_by_role}"`);
        });
        lines.push('');
        lines.push('"=== STAFF RESPONSE PERFORMANCE ==="');
        lines.push('"Staff Member","Role","Replies Handled","Avg Delay","Fast Responses (<5m %)","Max Delay"');
        (reportData.delayed_reports.staff_performance || []).forEach(s => {
          lines.push(`"${s.staff_name}","${s.staff_role}","${s.replies_count}","${s.avg_delay_formatted}","${s.fast_percent}%","${s.max_delay_formatted}"`);
        });
      }

      const csvContent = '\uFEFF' + lines.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Activity_Report_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('CSV Report downloaded successfully');
    } catch (err) {
      console.error('Failed to export CSV:', err);
      toast.error('Failed to generate CSV');
    } finally {
      setExportingCsv(false);
    }
  };

  // PDF Export Handler (Dynamic import for fast page load)
  const handleDownloadPdf = async () => {
    if (!reportData) return;
    setExportingPdf(true);
    try {
      const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
        import('jspdf'),
        import('jspdf-autotable')
      ]);
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const { metadata, metrics, daily_breakdown, whatsapp_details, bot_details } = reportData;

      // Header Banner
      doc.setFillColor(15, 23, 42); // slate-900
      doc.rect(0, 0, 210, 28, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('ACTIVITY & PERFORMANCE REPORT', 14, 14);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(148, 163, 184); // slate-400
      doc.text(`Generated: ${new Date().toLocaleString()} | Timezone: IST`, 14, 21);

      // Filter Metadata Box
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('REPORT PARAMETERS & SCOPE', 14, 35);

      const filterRows = [
        ['Date Range Filter', metadata?.range_label || datePreset],
        ['Admin Scope', adminId === 'all' ? 'All Admins (Global View)' : `Admin ID: ${adminId}`],
        ['WhatsApp Channels', selectedWaIds.length > 0 ? `${selectedWaIds.length} Selected` : 'All Available Channels'],
        ['Telegram Bots', selectedBotIds.length > 0 ? `${selectedBotIds.length} Selected` : 'All Available Bots']
      ];

      autoTable(doc, {
        startY: 38,
        head: [['Filter Parameter', 'Active Setting']],
        body: filterRows,
        theme: 'striped',
        headStyles: { fillColor: [51, 65, 85], textColor: 255, fontSize: 8 },
        bodyStyles: { fontSize: 8, textColor: 50 },
        margin: { left: 14, right: 14 }
      });

      // Executive Metrics Summary
      const summaryStartY = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('EXECUTIVE KPI SUMMARY', 14, summaryStartY);

      const summaryTableData = [
        [
          'WhatsApp: New Users / Contacts',
          String(metrics?.today?.whatsapp?.users_joined || 0),
          String(metrics?.yesterday?.whatsapp?.users_joined || 0),
          String(metrics?.period?.whatsapp?.users_joined || 0),
          String(metrics?.all_time?.whatsapp?.users_joined || 0)
        ],
        [
          'WhatsApp: Incoming Messages',
          String(metrics?.today?.whatsapp?.incoming_msgs || 0),
          String(metrics?.yesterday?.whatsapp?.incoming_msgs || 0),
          String(metrics?.period?.whatsapp?.incoming_msgs || 0),
          String(metrics?.all_time?.whatsapp?.incoming_msgs || 0)
        ],
        [
          'WhatsApp: Outgoing Messages',
          String(metrics?.today?.whatsapp?.outgoing_msgs || 0),
          String(metrics?.yesterday?.whatsapp?.outgoing_msgs || 0),
          String(metrics?.period?.whatsapp?.outgoing_msgs || 0),
          String(metrics?.all_time?.whatsapp?.outgoing_msgs || 0)
        ],
        [
          'WhatsApp: Total Messages',
          String(metrics?.today?.whatsapp?.total_msgs || 0),
          String(metrics?.yesterday?.whatsapp?.total_msgs || 0),
          String(metrics?.period?.whatsapp?.total_msgs || 0),
          String(metrics?.all_time?.whatsapp?.total_msgs || 0)
        ],
        [
          'Telegram: Bot Users Joined',
          String(metrics?.today?.bots?.users_joined || 0),
          String(metrics?.yesterday?.bots?.users_joined || 0),
          String(metrics?.period?.bots?.users_joined || 0),
          String(metrics?.all_time?.bots?.users_joined || 0)
        ],
        [
          'Telegram: Incoming Messages',
          String(metrics?.today?.bots?.incoming_msgs || 0),
          String(metrics?.yesterday?.bots?.incoming_msgs || 0),
          String(metrics?.period?.bots?.incoming_msgs || 0),
          String(metrics?.all_time?.bots?.incoming_msgs || 0)
        ],
        [
          'Telegram: Outgoing Messages',
          String(metrics?.today?.bots?.outgoing_msgs || 0),
          String(metrics?.yesterday?.bots?.outgoing_msgs || 0),
          String(metrics?.period?.bots?.outgoing_msgs || 0),
          String(metrics?.all_time?.bots?.outgoing_msgs || 0)
        ],
        [
          'Telegram: Total Messages',
          String(metrics?.today?.bots?.total_msgs || 0),
          String(metrics?.yesterday?.bots?.total_msgs || 0),
          String(metrics?.period?.bots?.total_msgs || 0),
          String(metrics?.all_time?.bots?.total_msgs || 0)
        ],
        [
          'COMBINED: Total New Users',
          String(metrics?.today?.combined?.users_joined || 0),
          String(metrics?.yesterday?.combined?.users_joined || 0),
          String(metrics?.period?.combined?.users_joined || 0),
          String(metrics?.all_time?.combined?.users_joined || 0)
        ],
        [
          'COMBINED: Total Messages',
          String(metrics?.today?.combined?.total_msgs || 0),
          String(metrics?.yesterday?.combined?.total_msgs || 0),
          String(metrics?.period?.combined?.total_msgs || 0),
          String(metrics?.all_time?.combined?.total_msgs || 0)
        ]
      ];

      autoTable(doc, {
        startY: summaryStartY + 3,
        head: [['Metric', 'Today', 'Yesterday', 'Period Total', 'All Time']],
        body: summaryTableData,
        theme: 'grid',
        headStyles: { fillColor: [16, 185, 129], textColor: 255, fontSize: 8 },
        bodyStyles: { fontSize: 8 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: 14, right: 14 }
      });

      // Daily Breakdown Table
      const dailyStartY = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('DAILY ACTIVITY TIMELINE', 14, dailyStartY);

      const dailyRows = (daily_breakdown || []).slice(0, 31).map(r => [
        r.date,
        String(r.wa_users),
        String(r.wa_incoming),
        String(r.wa_outgoing),
        String(r.bot_users),
        String(r.bot_incoming),
        String(r.bot_outgoing),
        String(r.total_messages)
      ]);

      autoTable(doc, {
        startY: dailyStartY + 3,
        head: [['Date', 'WA Users', 'WA In', 'WA Out', 'Bot Users', 'Bot In', 'Bot Out', 'Total Msgs']],
        body: dailyRows.length > 0 ? dailyRows : [['No activity records found', '-', '-', '-', '-', '-', '-', '-']],
        theme: 'striped',
        headStyles: { fillColor: [14, 116, 144], textColor: 255, fontSize: 7.5 },
        bodyStyles: { fontSize: 7.5 },
        margin: { left: 14, right: 14 }
      });

      // WhatsApp Channel Breakdown Table
      if ((whatsapp_details || []).length > 0) {
        const waStartY = doc.lastAutoTable.finalY + 8;
        if (waStartY > 240) doc.addPage();
        const currentY = waStartY > 240 ? 16 : waStartY;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('WHATSAPP CHANNELS BREAKDOWN', 14, currentY);

        const waRows = whatsapp_details.map(w => [
          w.display_name,
          w.phone_number_id,
          String(w.period_users),
          String(w.period_incoming),
          String(w.period_outgoing),
          String(w.period_total)
        ]);

        autoTable(doc, {
          startY: currentY + 3,
          head: [['Channel Name', 'Phone ID', 'Users Joined', 'Incoming', 'Outgoing', 'Total Msgs']],
          body: waRows,
          theme: 'grid',
          headStyles: { fillColor: [37, 211, 102], textColor: 255, fontSize: 8 },
          bodyStyles: { fontSize: 8 },
          margin: { left: 14, right: 14 }
        });
      }

      // Telegram Bots Breakdown Table
      if ((bot_details || []).length > 0) {
        const botStartY = doc.lastAutoTable.finalY + 8;
        if (botStartY > 240) doc.addPage();
        const currentY = botStartY > 240 ? 16 : botStartY;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('TELEGRAM BOTS BREAKDOWN', 14, currentY);

        const botRows = bot_details.map(b => [
          b.bot_name || b.name || 'Telegram Bot',
          b.bot_username || b.username ? `@${b.bot_username || b.username}` : '-',
          String(b.period_users),
          String(b.period_incoming),
          String(b.period_outgoing),
          String(b.period_total)
        ]);

        autoTable(doc, {
          startY: currentY + 3,
          head: [['Bot Name', 'Username', 'Users Joined', 'Incoming', 'Outgoing', 'Total Msgs']],
          body: botRows,
          theme: 'grid',
          headStyles: { fillColor: [41, 155, 237], textColor: 255, fontSize: 8 },
          bodyStyles: { fontSize: 8 },
          margin: { left: 14, right: 14 }
        });
      }

      // Response Delay & Latency Summary in PDF
      if (reportData?.delayed_reports?.summary) {
        const dSum = reportData.delayed_reports.summary;
        const delayStartY = doc.lastAutoTable.finalY + 8;
        if (delayStartY > 220) doc.addPage();
        const currentY = delayStartY > 220 ? 16 : delayStartY;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('RESPONSE DELAY & REPLY LATENCY (Sending Time - Incoming Time)', 14, currentY);

        const delaySummaryRows = [
          ['Average Response Delay', dSum.avg_delay_formatted || '0s'],
          ['Median Response Delay', dSum.median_delay_formatted || '0s'],
          ['Worst / Max Delay', dSum.max_delay_formatted || '0s'],
          ['Total Replies Analyzed', String(dSum.total_replies || 0)],
          ['Fast Replies (< 5 mins)', `${dSum.fast_count || 0} (${dSum.fast_percent || 0}%)`],
          ['Delayed Replies (> 15 mins)', `${(dSum.delayed_count || 0) + (dSum.critical_count || 0)}`],
          ['Currently Unanswered / Pending', String(dSum.pending_unanswered_count || 0)],
          ['WhatsApp Average Delay', dSum.whatsapp?.avg_delay_formatted || '0s'],
          ['Telegram Bot Average Delay', dSum.bots?.avg_delay_formatted || '0s']
        ];

        autoTable(doc, {
          startY: currentY + 3,
          head: [['Latency Metric', 'Value']],
          body: delaySummaryRows,
          theme: 'grid',
          headStyles: { fillColor: [245, 158, 11], textColor: 255, fontSize: 8 },
          bodyStyles: { fontSize: 8 },
          margin: { left: 14, right: 14 }
        });
      }

      // Footer with page numbers
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        doc.text(`Page ${i} of ${totalPages}`, 105, 290, { align: 'center' });
      }

      doc.save(`Activity_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('PDF Report downloaded successfully');
    } catch (err) {
      console.error('Failed to generate PDF:', err);
      toast.error('Failed to generate PDF report');
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="space-y-6 pb-12" data-testid="reports-page">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
              <FileText size={22} weight="duotone" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
                Reports & Analytics
              </h1>
              <p className="text-sm text-muted-foreground">
                Track users joined, incoming/outgoing message traffic across WhatsApp and Telegram bots.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons: PDF, CSV, Refresh */}
        <div className="flex items-center flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchReport}
            disabled={loading}
            className="gap-2"
          >
            <ArrowClockwise size={16} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadCsv}
            disabled={loading || exportingCsv}
            className="gap-2 border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10 dark:text-emerald-400"
          >
            <FileCsv size={18} weight="duotone" />
            <span>{exportingCsv ? 'Generating...' : 'Download CSV'}</span>
          </Button>

          <Button
            variant="default"
            size="sm"
            onClick={handleDownloadPdf}
            disabled={loading || exportingPdf}
            className="gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-sm"
          >
            <FilePdf size={18} weight="fill" />
            <span>{exportingPdf ? 'Exporting PDF...' : 'Download PDF'}</span>
          </Button>
        </div>
      </div>

      {/* Filter Bar */}
      <Card className="border-border/60 shadow-sm bg-card/50 backdrop-blur-sm">
        <CardContent className="p-4 sm:p-5 space-y-4">
          <div className="flex items-center justify-between pb-2 border-b border-border/40">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Funnel size={14} weight="bold" />
              <span>Filters & Aggregation Scope</span>
            </div>
            {(adminId !== 'all' || datePreset !== 'today' || selectedWaIds.length > 0 || selectedBotIds.length > 0) && (
              <button
                onClick={resetAllFilters}
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                <X size={12} /> Reset Filters
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* 1. Admin Filter */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <ShieldCheck size={14} className="text-primary" /> Admin Account
              </Label>
              {isSuperAdmin ? (
                <select
                  value={adminId}
                  onChange={(e) => setAdminId(e.target.value)}
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="all">All Admins (Global View)</option>
                  {(filterOptions.admins || []).map((adm) => (
                    <option key={adm.id} value={adm.id}>
                      {adm.name || adm.email} ({adm.role})
                    </option>
                  ))}
                </select>
              ) : (
                <div className="h-9 px-3 flex items-center rounded-md border border-input bg-muted/40 text-sm text-muted-foreground">
                  {user?.name || user?.email} (Your Account)
                </div>
              )}
            </div>

            {/* 2. Date Preset Filter */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Calendar size={14} className="text-primary" /> Date Period
              </Label>
              <select
                value={datePreset}
                onChange={(e) => setDatePreset(e.target.value)}
                className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="today">Today (IST)</option>
                <option value="yesterday">Yesterday</option>
                <option value="last_7_days">Last 7 Days</option>
                <option value="last_30_days">Last 30 Days</option>
                <option value="this_month">This Month</option>
                <option value="all_time">All Time</option>
                <option value="custom">Custom Date Range</option>
              </select>
            </div>

            {/* 3. WhatsApp Multi-Select Popover */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <WhatsappLogo size={14} className="text-emerald-500" /> WhatsApp Channels
              </Label>
              <Popover open={waPopoverOpen} onOpenChange={setWaPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full h-9 px-3 justify-between font-normal text-sm bg-background border-input"
                  >
                    <span className="truncate">
                      {selectedWaIds.length === 0
                        ? 'All WhatsApp Channels'
                        : `${selectedWaIds.length} Channel${selectedWaIds.length > 1 ? 's' : ''} Selected`}
                    </span>
                    <CaretDown size={14} className="opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-3 space-y-3" align="start">
                  <div className="flex items-center justify-between border-b pb-2">
                    <span className="text-xs font-semibold">Select WhatsApp Channels</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleSelectAllWa}
                        className="text-[11px] text-primary hover:underline"
                      >
                        All
                      </button>
                      <span className="text-muted-foreground text-xs">•</span>
                      <button
                        onClick={handleClearWa}
                        className="text-[11px] text-muted-foreground hover:text-foreground"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <Input
                    placeholder="Search channel or phone..."
                    value={waSearch}
                    onChange={(e) => setWaSearch(e.target.value)}
                    className="h-8 text-xs"
                  />

                  <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                    {filteredWaList.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-2">No channels found</p>
                    ) : (
                      filteredWaList.map((api) => {
                        const isChecked = selectedWaIds.includes(api.id);
                        return (
                          <div
                            key={api.id}
                            onClick={() => handleToggleWa(api.id)}
                            className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer text-xs"
                          >
                            <Checkbox checked={isChecked} onCheckedChange={() => handleToggleWa(api.id)} />
                            <div className="truncate">
                              <p className="font-medium truncate">{api.display_name || 'WhatsApp Channel'}</p>
                              <p className="text-[10px] text-muted-foreground truncate">{api.phone_number_id || api.waba_id}</p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* 4. Telegram Bots Multi-Select Popover */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Robot size={14} className="text-sky-500" /> Telegram Bots
              </Label>
              <Popover open={botPopoverOpen} onOpenChange={setBotPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full h-9 px-3 justify-between font-normal text-sm bg-background border-input"
                  >
                    <span className="truncate">
                      {selectedBotIds.length === 0
                        ? 'All Telegram Bots'
                        : `${selectedBotIds.length} Bot${selectedBotIds.length > 1 ? 's' : ''} Selected`}
                    </span>
                    <CaretDown size={14} className="opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-3 space-y-3" align="start">
                  <div className="flex items-center justify-between border-b pb-2">
                    <span className="text-xs font-semibold">Select Telegram Bots</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleSelectAllBots}
                        className="text-[11px] text-primary hover:underline"
                      >
                        All
                      </button>
                      <span className="text-muted-foreground text-xs">•</span>
                      <button
                        onClick={handleClearBots}
                        className="text-[11px] text-muted-foreground hover:text-foreground"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <Input
                    placeholder="Search bot name or username..."
                    value={botSearch}
                    onChange={(e) => setBotSearch(e.target.value)}
                    className="h-8 text-xs"
                  />

                  <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                    {filteredBotList.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-2">No bots found</p>
                    ) : (
                      filteredBotList.map((bot) => {
                        const isChecked = selectedBotIds.includes(bot.id);
                        return (
                          <div
                            key={bot.id}
                            onClick={() => handleToggleBot(bot.id)}
                            className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer text-xs"
                          >
                            <Checkbox checked={isChecked} onCheckedChange={() => handleToggleBot(bot.id)} />
                            <div className="truncate">
                              <p className="font-medium truncate">{bot.bot_name || bot.name || bot.bot_username || bot.username || 'Telegram Bot'}</p>
                              {(bot.bot_username || bot.username) && <p className="text-[10px] text-muted-foreground">@{bot.bot_username || bot.username}</p>}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Custom Date Pickers if 'custom' selected */}
          {datePreset === 'custom' && (
            <div className="pt-2 border-t border-border/40 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground font-medium">From:</span>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-8 w-40 text-xs"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground font-medium">To:</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-8 w-40 text-xs"
                />
              </div>
              <Button size="sm" variant="secondary" className="h-8 text-xs" onClick={fetchReport}>
                Apply Range
              </Button>
            </div>
          )}

          {/* Active Filter Indicators */}
          <div className="flex flex-wrap items-center gap-1.5 pt-1 text-xs">
            <span className="text-muted-foreground">Active Filter:</span>
            <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20">
              {metadata?.range_label || datePreset}
            </Badge>
            {adminId !== 'all' && (
              <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/20">
                Admin: {(filterOptions.admins || []).find(a => a.id === adminId)?.name || adminId}
              </Badge>
            )}
            {selectedWaIds.length > 0 && (
              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">
                WhatsApp: {selectedWaIds.length} channel{selectedWaIds.length > 1 ? 's' : ''}
              </Badge>
            )}
            {selectedBotIds.length > 0 && (
              <Badge variant="outline" className="bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20">
                Bots: {selectedBotIds.length} bot{selectedBotIds.length > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* KPI Overview Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* WhatsApp Card */}
        <Card className="border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.04] to-transparent shadow-sm">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                <WhatsappLogo size={18} weight="fill" />
              </div>
              <div>
                <CardTitle className="text-base font-bold">WhatsApp Traffic</CardTitle>
                <p className="text-xs text-muted-foreground">WABA Customer & Message Metrics</p>
              </div>
            </div>
            <Badge variant="outline" className="text-emerald-600 dark:text-emerald-400 border-emerald-500/30 bg-emerald-500/5">
              Live
            </Badge>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div className="p-3 rounded-lg bg-background/80 border border-border/50">
                <p className="text-[11px] text-muted-foreground font-medium flex items-center gap-1">
                  <Users size={12} className="text-emerald-500" /> Users Joined
                </p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold tracking-tight">
                    {metrics?.period?.whatsapp?.users_joined ?? 0}
                  </span>
                  <span className="text-[11px] text-muted-foreground">in range</span>
                </div>
                <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-2 pt-2 border-t border-border/40">
                  <span>Today: <strong className="text-foreground">{metrics?.today?.whatsapp?.users_joined ?? 0}</strong></span>
                  <span>Yesterday: <strong className="text-foreground">{metrics?.yesterday?.whatsapp?.users_joined ?? 0}</strong></span>
                  <span>All: <strong className="text-foreground">{metrics?.all_time?.whatsapp?.users_joined ?? 0}</strong></span>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-background/80 border border-border/50">
                <p className="text-[11px] text-muted-foreground font-medium flex items-center gap-1">
                  <ChatTeardropText size={12} className="text-emerald-500" /> Total Msgs
                </p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold tracking-tight">
                    {metrics?.period?.whatsapp?.total_msgs ?? 0}
                  </span>
                  <span className="text-[11px] text-muted-foreground">in range</span>
                </div>
                <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-2 pt-2 border-t border-border/40">
                  <span>Today: <strong className="text-foreground">{metrics?.today?.whatsapp?.total_msgs ?? 0}</strong></span>
                  <span>Yesterday: <strong className="text-foreground">{metrics?.yesterday?.whatsapp?.total_msgs ?? 0}</strong></span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center justify-between p-2 rounded bg-muted/30">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <ArrowDownLeft size={13} className="text-emerald-500" /> Incoming
                </span>
                <span className="font-semibold">{metrics?.period?.whatsapp?.incoming_msgs ?? 0}</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded bg-muted/30">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <ArrowUpRight size={13} className="text-teal-500" /> Outgoing
                </span>
                <span className="font-semibold">{metrics?.period?.whatsapp?.outgoing_msgs ?? 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Telegram Bots Card */}
        <Card className="border-sky-500/20 bg-gradient-to-br from-sky-500/[0.04] to-transparent shadow-sm">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-sky-500/15 text-sky-600 dark:text-sky-400 flex items-center justify-center">
                <Robot size={18} weight="fill" />
              </div>
              <div>
                <CardTitle className="text-base font-bold">Telegram Bot Traffic</CardTitle>
                <p className="text-xs text-muted-foreground">User Onboarding & Bot Messages</p>
              </div>
            </div>
            <Badge variant="outline" className="text-sky-600 dark:text-sky-400 border-sky-500/30 bg-sky-500/5">
              Live
            </Badge>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div className="p-3 rounded-lg bg-background/80 border border-border/50">
                <p className="text-[11px] text-muted-foreground font-medium flex items-center gap-1">
                  <Users size={12} className="text-sky-500" /> Users Joined
                </p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold tracking-tight">
                    {metrics?.period?.bots?.users_joined ?? 0}
                  </span>
                  <span className="text-[11px] text-muted-foreground">in range</span>
                </div>
                <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-2 pt-2 border-t border-border/40">
                  <span>Today: <strong className="text-foreground">{metrics?.today?.bots?.users_joined ?? 0}</strong></span>
                  <span>Yesterday: <strong className="text-foreground">{metrics?.yesterday?.bots?.users_joined ?? 0}</strong></span>
                  <span>All: <strong className="text-foreground">{metrics?.all_time?.bots?.users_joined ?? 0}</strong></span>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-background/80 border border-border/50">
                <p className="text-[11px] text-muted-foreground font-medium flex items-center gap-1">
                  <ChatTeardropText size={12} className="text-sky-500" /> Total Msgs
                </p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold tracking-tight">
                    {metrics?.period?.bots?.total_msgs ?? 0}
                  </span>
                  <span className="text-[11px] text-muted-foreground">in range</span>
                </div>
                <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-2 pt-2 border-t border-border/40">
                  <span>Today: <strong className="text-foreground">{metrics?.today?.bots?.total_msgs ?? 0}</strong></span>
                  <span>Yesterday: <strong className="text-foreground">{metrics?.yesterday?.bots?.total_msgs ?? 0}</strong></span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center justify-between p-2 rounded bg-muted/30">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <ArrowDownLeft size={13} className="text-sky-500" /> Incoming
                </span>
                <span className="font-semibold">{metrics?.period?.bots?.incoming_msgs ?? 0}</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded bg-muted/30">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <ArrowUpRight size={13} className="text-indigo-500" /> Outgoing
                </span>
                <span className="font-semibold">{metrics?.period?.bots?.outgoing_msgs ?? 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Combined Overall Card */}
        <Card className="border-primary/20 bg-gradient-to-br from-primary/[0.05] to-transparent shadow-sm">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center">
                <ChatTeardropText size={18} weight="fill" />
              </div>
              <div>
                <CardTitle className="text-base font-bold">Total Operations</CardTitle>
                <p className="text-xs text-muted-foreground">WhatsApp + Bots Aggregation</p>
              </div>
            </div>
            <Badge variant="outline" className="text-primary border-primary/30 bg-primary/5">
              Combined
            </Badge>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div className="p-3 rounded-lg bg-background/80 border border-border/50">
                <p className="text-[11px] text-muted-foreground font-medium flex items-center gap-1">
                  <Users size={12} className="text-primary" /> Total Joined
                </p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold tracking-tight text-primary">
                    {metrics?.period?.combined?.users_joined ?? 0}
                  </span>
                  <span className="text-[11px] text-muted-foreground">in range</span>
                </div>
                <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-2 pt-2 border-t border-border/40">
                  <span>Today: <strong className="text-foreground">{metrics?.today?.combined?.users_joined ?? 0}</strong></span>
                  <span>Yesterday: <strong className="text-foreground">{metrics?.yesterday?.combined?.users_joined ?? 0}</strong></span>
                  <span>All: <strong className="text-foreground">{metrics?.all_time?.combined?.users_joined ?? 0}</strong></span>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-background/80 border border-border/50">
                <p className="text-[11px] text-muted-foreground font-medium flex items-center gap-1">
                  <ChatTeardropText size={12} className="text-primary" /> Total Msgs
                </p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold tracking-tight text-primary">
                    {metrics?.period?.combined?.total_msgs ?? 0}
                  </span>
                  <span className="text-[11px] text-muted-foreground">in range</span>
                </div>
                <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-2 pt-2 border-t border-border/40">
                  <span>Today: <strong className="text-foreground">{metrics?.today?.combined?.total_msgs ?? 0}</strong></span>
                  <span>Yesterday: <strong className="text-foreground">{metrics?.yesterday?.combined?.total_msgs ?? 0}</strong></span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center justify-between p-2 rounded bg-muted/30">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <ArrowDownLeft size={13} className="text-emerald-500" /> Total In
                </span>
                <span className="font-semibold">{metrics?.period?.combined?.incoming_msgs ?? 0}</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded bg-muted/30">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <ArrowUpRight size={13} className="text-teal-500" /> Total Out
                </span>
                <span className="font-semibold">{metrics?.period?.combined?.outgoing_msgs ?? 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Detailed Breakdowns */}
      <div className="space-y-4">
        <div className="flex items-center gap-1 p-1 bg-muted/50 rounded-lg w-fit border border-border/40">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activeTab === 'overview'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Daily Timeline
          </button>
          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activeTab === 'whatsapp'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            WhatsApp Channels ({reportData?.whatsapp_details?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('bots')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activeTab === 'bots'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Telegram Bots ({reportData?.bot_details?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('delay')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${
              activeTab === 'delay'
                ? 'bg-background text-foreground shadow-sm font-semibold'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Timer size={14} className="text-amber-500" />
            Response Delay ({reportData?.delayed_reports?.summary?.total_replies || 0})
          </button>
          <button
            onClick={() => setActiveTab('recent')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activeTab === 'recent'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Recent Activity Preview
          </button>
        </div>

        {/* Tab 1: Daily Timeline */}
        {activeTab === 'overview' && (
          <div className="border border-border rounded-lg overflow-hidden bg-card">
            <div className="px-4 py-3 bg-muted/40 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">Daily Activity Breakdown</h3>
                <p className="text-xs text-muted-foreground">Showing date-by-date traffic in the selected timeframe</p>
              </div>
              <Badge variant="outline" className="text-xs">
                {reportData?.daily_breakdown?.length || 0} active day{reportData?.daily_breakdown?.length !== 1 ? 's' : ''}
              </Badge>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[700px]">
                <thead>
                  <tr className="bg-muted/30 border-b border-border text-muted-foreground">
                    <th className="text-left px-4 py-3 font-semibold">Date</th>
                    <th className="text-right px-3 py-3 font-semibold text-emerald-600 dark:text-emerald-400">WA Users</th>
                    <th className="text-right px-3 py-3 font-semibold text-emerald-600 dark:text-emerald-400">WA In</th>
                    <th className="text-right px-3 py-3 font-semibold text-emerald-600 dark:text-emerald-400">WA Out</th>
                    <th className="text-right px-3 py-3 font-semibold text-emerald-600 dark:text-emerald-400">WA Total</th>
                    <th className="text-right px-3 py-3 font-semibold text-sky-600 dark:text-sky-400">Bot Users</th>
                    <th className="text-right px-3 py-3 font-semibold text-sky-600 dark:text-sky-400">Bot In</th>
                    <th className="text-right px-3 py-3 font-semibold text-sky-600 dark:text-sky-400">Bot Out</th>
                    <th className="text-right px-3 py-3 font-semibold text-sky-600 dark:text-sky-400">Bot Total</th>
                    <th className="text-right px-4 py-3 font-semibold text-foreground">Grand Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {(reportData?.daily_breakdown || []).length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">
                        No activity recorded for this period or filters.
                      </td>
                    </tr>
                  ) : (
                    (reportData?.daily_breakdown || []).map((row) => (
                      <tr key={row.date} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-2.5 font-medium">{row.date}</td>
                        <td className="px-3 py-2.5 text-right">{row.wa_users}</td>
                        <td className="px-3 py-2.5 text-right">{row.wa_incoming}</td>
                        <td className="px-3 py-2.5 text-right">{row.wa_outgoing}</td>
                        <td className="px-3 py-2.5 text-right font-medium text-emerald-600 dark:text-emerald-400">{row.wa_total}</td>
                        <td className="px-3 py-2.5 text-right">{row.bot_users}</td>
                        <td className="px-3 py-2.5 text-right">{row.bot_incoming}</td>
                        <td className="px-3 py-2.5 text-right">{row.bot_outgoing}</td>
                        <td className="px-3 py-2.5 text-right font-medium text-sky-600 dark:text-sky-400">{row.bot_total}</td>
                        <td className="px-4 py-2.5 text-right font-bold text-foreground">{row.total_messages}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab 2: WhatsApp Channels */}
        {activeTab === 'whatsapp' && (
          <div className="border border-border rounded-lg overflow-hidden bg-card">
            <div className="px-4 py-3 bg-muted/40 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">WhatsApp Channels Breakdown</h3>
                <p className="text-xs text-muted-foreground">Performance of individual WABA phone numbers and accounts</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[600px]">
                <thead>
                  <tr className="bg-muted/30 border-b border-border text-muted-foreground">
                    <th className="text-left px-4 py-3 font-semibold">Channel Name</th>
                    <th className="text-left px-3 py-3 font-semibold">Phone ID / WABA</th>
                    <th className="text-right px-3 py-3 font-semibold">Period Users</th>
                    <th className="text-right px-3 py-3 font-semibold">Incoming Msgs</th>
                    <th className="text-right px-3 py-3 font-semibold">Outgoing Msgs</th>
                    <th className="text-right px-3 py-3 font-semibold">Period Total</th>
                    <th className="text-right px-3 py-3 font-semibold">Today Users</th>
                    <th className="text-right px-4 py-3 font-semibold">All Time Users</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {(reportData?.whatsapp_details || []).length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                        No WhatsApp APIs configured or found.
                      </td>
                    </tr>
                  ) : (
                    (reportData?.whatsapp_details || []).map((wa) => (
                      <tr key={wa.api_id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-2.5 font-medium flex items-center gap-2">
                          <WhatsappLogo size={15} className="text-emerald-500" />
                          <span>{wa.display_name}</span>
                        </td>
                        <td className="px-3 py-2.5 text-muted-foreground font-mono">{wa.phone_number_id || wa.waba_id}</td>
                        <td className="px-3 py-2.5 text-right font-semibold text-emerald-600 dark:text-emerald-400">{wa.period_users}</td>
                        <td className="px-3 py-2.5 text-right">{wa.period_incoming}</td>
                        <td className="px-3 py-2.5 text-right">{wa.period_outgoing}</td>
                        <td className="px-3 py-2.5 text-right font-bold">{wa.period_total}</td>
                        <td className="px-3 py-2.5 text-right">{wa.today_users}</td>
                        <td className="px-4 py-2.5 text-right text-muted-foreground">{wa.all_time_users}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab 3: Telegram Bots */}
        {activeTab === 'bots' && (
          <div className="border border-border rounded-lg overflow-hidden bg-card">
            <div className="px-4 py-3 bg-muted/40 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">Telegram Bots Breakdown</h3>
                <p className="text-xs text-muted-foreground">Performance of individual Telegram bots</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[600px]">
                <thead>
                  <tr className="bg-muted/30 border-b border-border text-muted-foreground">
                    <th className="text-left px-4 py-3 font-semibold">Bot Name</th>
                    <th className="text-left px-3 py-3 font-semibold">Username</th>
                    <th className="text-right px-3 py-3 font-semibold">Period Users</th>
                    <th className="text-right px-3 py-3 font-semibold">Incoming Msgs</th>
                    <th className="text-right px-3 py-3 font-semibold">Outgoing Msgs</th>
                    <th className="text-right px-3 py-3 font-semibold">Period Total</th>
                    <th className="text-right px-3 py-3 font-semibold">Today Users</th>
                    <th className="text-right px-4 py-3 font-semibold">All Time Users</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {(reportData?.bot_details || []).length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                        No Telegram Bots found.
                      </td>
                    </tr>
                  ) : (
                    (reportData?.bot_details || []).map((bot) => (
                      <tr key={bot.bot_id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-2.5 font-medium flex items-center gap-2">
                          <Robot size={15} className="text-sky-500" />
                          <span>{bot.bot_name || bot.name || 'Telegram Bot'}</span>
                        </td>
                        <td className="px-3 py-2.5 text-muted-foreground font-mono">
                          {bot.bot_username || bot.username ? `@${bot.bot_username || bot.username}` : '-'}
                        </td>
                        <td className="px-3 py-2.5 text-right font-semibold text-sky-600 dark:text-sky-400">{bot.period_users}</td>
                        <td className="px-3 py-2.5 text-right">{bot.period_incoming}</td>
                        <td className="px-3 py-2.5 text-right">{bot.period_outgoing}</td>
                        <td className="px-3 py-2.5 text-right font-bold">{bot.period_total}</td>
                        <td className="px-3 py-2.5 text-right">{bot.today_users}</td>
                        <td className="px-4 py-2.5 text-right text-muted-foreground">{bot.all_time_users}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab 4: Recent Users & Contacts Preview */}
        {activeTab === 'recent' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* WhatsApp Contacts */}
            <div className="border border-border rounded-lg overflow-hidden bg-card">
              <div className="px-4 py-3 bg-muted/40 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <WhatsappLogo size={16} className="text-emerald-500" />
                  <h3 className="text-xs font-semibold">Latest WhatsApp Contacts Joined</h3>
                </div>
                <Badge variant="outline" className="text-[10px]">
                  {reportData?.recent_whatsapp_contacts?.length || 0} shown
                </Badge>
              </div>
              <div className="max-h-80 overflow-y-auto divide-y divide-border/50 text-xs">
                {(reportData?.recent_whatsapp_contacts || []).length === 0 ? (
                  <p className="p-4 text-center text-muted-foreground">No contacts joined in this period</p>
                ) : (
                  (reportData?.recent_whatsapp_contacts || []).map((c) => (
                    <div key={c.id || c.phone} className="p-3 flex items-center justify-between hover:bg-muted/20">
                      <div>
                        <p className="font-medium text-foreground">{c.name || 'WhatsApp Contact'}</p>
                        <p className="text-[11px] text-muted-foreground font-mono">{c.phone}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-[10px] text-muted-foreground">
                          {c.created_at || c.joined_at ? new Date(c.created_at || c.joined_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Bot Users */}
            <div className="border border-border rounded-lg overflow-hidden bg-card">
              <div className="px-4 py-3 bg-muted/40 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Robot size={16} className="text-sky-500" />
                  <h3 className="text-xs font-semibold">Latest Bot Users Joined</h3>
                </div>
                <Badge variant="outline" className="text-[10px]">
                  {reportData?.recent_bot_users?.length || 0} shown
                </Badge>
              </div>
              <div className="max-h-80 overflow-y-auto divide-y divide-border/50 text-xs">
                {(reportData?.recent_bot_users || []).length === 0 ? (
                  <p className="p-4 text-center text-muted-foreground">No bot users joined in this period</p>
                ) : (
                  (reportData?.recent_bot_users || []).map((u, i) => (
                    <div key={u.telegram_user_id || i} className="p-3 flex items-center justify-between hover:bg-muted/20">
                      <div>
                        <p className="font-medium text-foreground">
                          {u.first_name || (u.username ? `@${u.username}` : `User ${u.telegram_user_id}`)}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {u.bot_name || 'Telegram Bot'} {u.username && `• @${u.username}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-[10px] text-muted-foreground">
                          {u.joined_at || u.created_at ? new Date(u.joined_at || u.created_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tab 5: Response Delay / Reply Latency Report */}
        {activeTab === 'delay' && (
          <div className="space-y-4">
            {/* Delay Overview Card */}
            <div className="border border-border rounded-lg overflow-hidden bg-card">
              <div className="px-4 py-3 bg-muted/40 border-b border-border flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <Timer size={18} className="text-amber-500" />
                    <h3 className="text-sm font-semibold">Customer Response Delay & Reply Latency</h3>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Sending message time minus incoming message time (How much delayed to reply to a client)
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-xs font-normal border-emerald-500/30 text-emerald-600 dark:text-emerald-400 bg-emerald-500/5">
                    WhatsApp: {reportData?.delayed_reports?.summary?.whatsapp?.avg_delay_formatted || '0s'} avg ({reportData?.delayed_reports?.summary?.whatsapp?.total_replies || 0} replies)
                  </Badge>
                  <Badge variant="outline" className="text-xs font-normal border-sky-500/30 text-sky-600 dark:text-sky-400 bg-sky-500/5">
                    Telegram: {reportData?.delayed_reports?.summary?.bots?.avg_delay_formatted || '0s'} avg ({reportData?.delayed_reports?.summary?.bots?.total_replies || 0} replies)
                  </Badge>
                </div>
              </div>

              {/* KPI Grid */}
              <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 border-b border-border bg-background">
                {/* 1. Average Delay */}
                <div className="p-3 rounded-lg border border-border/60 bg-muted/20">
                  <div className="flex items-center justify-between text-muted-foreground text-xs font-medium mb-1">
                    <span>Average Delay</span>
                    <Clock size={14} className="text-amber-500" />
                  </div>
                  <div className="text-xl font-bold tracking-tight text-foreground">
                    {reportData?.delayed_reports?.summary?.avg_delay_formatted || '0s'}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1 truncate">
                    Median: {reportData?.delayed_reports?.summary?.median_delay_formatted || '0s'} • Max: {reportData?.delayed_reports?.summary?.max_delay_formatted || '0s'}
                  </div>
                </div>

                {/* 2. Fast Replies */}
                <div className="p-3 rounded-lg border border-border/60 bg-muted/20">
                  <div className="flex items-center justify-between text-muted-foreground text-xs font-medium mb-1">
                    <span>Fast (&lt; 5 mins)</span>
                    <Lightning size={14} className="text-emerald-500" />
                  </div>
                  <div className="text-xl font-bold tracking-tight text-emerald-600 dark:text-emerald-400">
                    {reportData?.delayed_reports?.summary?.fast_count || 0}
                    <span className="text-xs font-normal ml-1.5 opacity-80">
                      ({reportData?.delayed_reports?.summary?.fast_percent || 0}%)
                    </span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1 truncate">
                    Immediate client turnaround
                  </div>
                </div>

                {/* 3. Delayed Replies */}
                <div className="p-3 rounded-lg border border-border/60 bg-muted/20">
                  <div className="flex items-center justify-between text-muted-foreground text-xs font-medium mb-1">
                    <span>Delayed (&gt; 15 mins)</span>
                    <Hourglass size={14} className="text-orange-500" />
                  </div>
                  <div className="text-xl font-bold tracking-tight text-orange-600 dark:text-orange-400">
                    {(reportData?.delayed_reports?.summary?.delayed_count || 0) + (reportData?.delayed_reports?.summary?.critical_count || 0)}
                    <span className="text-xs font-normal ml-1.5 opacity-80">
                      ({reportData?.delayed_reports?.summary?.total_replies ? Math.round((((reportData.delayed_reports.summary.delayed_count || 0) + (reportData.delayed_reports.summary.critical_count || 0)) / reportData.delayed_reports.summary.total_replies) * 100) : 0}%)
                    </span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1 truncate">
                    {reportData?.delayed_reports?.summary?.critical_count || 0} critical (&gt; 1 hour)
                  </div>
                </div>

                {/* 4. Pending Unanswered */}
                <div className="p-3 rounded-lg border border-border/60 bg-muted/20">
                  <div className="flex items-center justify-between text-muted-foreground text-xs font-medium mb-1">
                    <span>Pending Unanswered</span>
                    <WarningCircle size={14} className="text-rose-500" />
                  </div>
                  <div className="text-xl font-bold tracking-tight text-rose-600 dark:text-rose-400">
                    {reportData?.delayed_reports?.summary?.pending_unanswered_count || 0}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1 truncate">
                    Awaiting staff response
                  </div>
                </div>
              </div>

              {/* Sub-tab Navigation and Filter Bar */}
              <div className="p-3 bg-muted/10 border-b border-border flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-md border border-border/40">
                  <button
                    onClick={() => setDelaySubTab('replies')}
                    className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${delaySubTab === 'replies' ? 'bg-background text-foreground shadow-xs font-semibold' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    Replied Logs ({filteredDelayReplies.length})
                  </button>
                  <button
                    onClick={() => setDelaySubTab('pending')}
                    className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${delaySubTab === 'pending' ? 'bg-background text-foreground shadow-xs font-semibold' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    Pending Clients ({filteredDelayPending.length})
                  </button>
                  <button
                    onClick={() => setDelaySubTab('staff')}
                    className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${delaySubTab === 'staff' ? 'bg-background text-foreground shadow-xs font-semibold' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    Staff Speed ({reportData?.delayed_reports?.staff_performance?.length || 0})
                  </button>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-2 flex-wrap flex-1 justify-end">
                  {/* Channel select */}
                  <select
                    value={delayChannelFilter}
                    onChange={e => setDelayChannelFilter(e.target.value)}
                    className="h-8 text-xs rounded-md border border-border bg-background px-2.5 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="all">All Channels</option>
                    <option value="whatsapp">WhatsApp Only</option>
                    <option value="bot">Telegram Bots Only</option>
                  </select>

                  {/* Severity select (only on replies and pending) */}
                  {delaySubTab !== 'staff' && (
                    <select
                      value={delaySeverityFilter}
                      onChange={e => setDelaySeverityFilter(e.target.value)}
                      className="h-8 text-xs rounded-md border border-border bg-background px-2.5 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      <option value="all">All Delays</option>
                      <option value="fast">Fast (&lt; 5m)</option>
                      <option value="moderate">Moderate (5m - 15m)</option>
                      <option value="delayed">Delayed (15m - 1h)</option>
                      <option value="critical">Critical (&gt; 1h)</option>
                    </select>
                  )}

                  {/* Search Input */}
                  <div className="relative min-w-[180px] max-w-xs">
                    <Input
                      value={delaySearch}
                      onChange={e => setDelaySearch(e.target.value)}
                      placeholder="Search client, staff, text..."
                      className="h-8 text-xs pl-8 pr-2.5"
                    />
                    <Funnel size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    {delaySearch && (
                      <button onClick={() => setDelaySearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Table Content */}
              {delaySubTab === 'replies' && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[850px]">
                    <thead>
                      <tr className="bg-muted/30 border-b border-border text-muted-foreground">
                        <th className="text-left px-4 py-3 font-semibold">Channel</th>
                        <th className="text-left px-3 py-3 font-semibold">Client Contact</th>
                        <th className="text-left px-3 py-3 font-semibold">Client Incoming Msg</th>
                        <th className="text-left px-3 py-3 font-semibold">Staff Reply Msg</th>
                        <th className="text-center px-3 py-3 font-semibold text-amber-600 dark:text-amber-400">Response Delay</th>
                        <th className="text-left px-4 py-3 font-semibold">Replied By</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                      {filteredDelayReplies.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                            No replied messages matched the selected filters.
                          </td>
                        </tr>
                      ) : (
                        filteredDelayReplies.map((r) => {
                          const isFast = r.delay_severity === 'fast';
                          const isMod = r.delay_severity === 'moderate';
                          const isDel = r.delay_severity === 'delayed';
                          const badgeColor = isFast
                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                            : isMod
                            ? 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/30'
                            : isDel
                            ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30'
                            : 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30';

                          return (
                            <tr key={r.id} className="hover:bg-muted/20 transition-colors">
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-1.5">
                                  {r.channel === 'whatsapp' ? (
                                    <WhatsappLogo size={15} weight="fill" className="text-green-500 shrink-0" />
                                  ) : (
                                    <Robot size={15} weight="fill" className="text-sky-500 shrink-0" />
                                  )}
                                  <span className="font-medium truncate max-w-[120px]">{r.channel_name}</span>
                                </div>
                              </td>
                              <td className="px-3 py-2.5">
                                <p className="font-medium text-foreground truncate max-w-[140px]">{r.client_name}</p>
                                <p className="text-[10px] text-muted-foreground font-mono">{r.client_id}</p>
                              </td>
                              <td className="px-3 py-2.5 max-w-[200px]">
                                <p className="truncate text-foreground font-normal">{r.incoming_text || '...'}</p>
                                <p className="text-[10px] text-muted-foreground mt-0.5">
                                  {r.incoming_time ? new Date(r.incoming_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : ''}
                                  {r.incoming_count > 1 && <span className="ml-1 px-1 bg-muted text-[9px] rounded font-mono font-medium">({r.incoming_count} msgs)</span>}
                                </p>
                              </td>
                              <td className="px-3 py-2.5 max-w-[200px]">
                                <p className="truncate text-foreground font-normal">{r.reply_text || '...'}</p>
                                <p className="text-[10px] text-muted-foreground mt-0.5">
                                  {r.reply_time ? new Date(r.reply_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : ''}
                                </p>
                              </td>
                              <td className="px-3 py-2.5 text-center">
                                <Badge variant="outline" className={`text-xs px-2.5 py-0.5 font-bold ${badgeColor}`}>
                                  ⏱️ {r.delay_formatted}
                                </Badge>
                              </td>
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-semibold text-foreground">{r.replied_by || 'Staff'}</span>
                                  {r.replied_by_role && (
                                    <span className="text-[9px] uppercase px-1.5 py-0.2 rounded bg-muted text-muted-foreground font-mono">
                                      {r.replied_by_role}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Sub-view 2: Pending Unanswered Table */}
              {delaySubTab === 'pending' && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[700px]">
                    <thead>
                      <tr className="bg-muted/30 border-b border-border text-muted-foreground">
                        <th className="text-left px-4 py-3 font-semibold">Channel</th>
                        <th className="text-left px-3 py-3 font-semibold">Client Contact</th>
                        <th className="text-left px-3 py-3 font-semibold">Unanswered Incoming Message</th>
                        <th className="text-left px-3 py-3 font-semibold">Received Time</th>
                        <th className="text-center px-3 py-3 font-semibold text-rose-600 dark:text-rose-400">Waiting Duration</th>
                        <th className="text-right px-4 py-3 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                      {filteredDelayPending.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                            🎉 No pending unanswered client messages! All incoming chats have been replied.
                          </td>
                        </tr>
                      ) : (
                        filteredDelayPending.map((p) => (
                          <tr key={p.id} className="hover:bg-muted/20 transition-colors">
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-1.5">
                                {p.channel === 'whatsapp' ? (
                                  <WhatsappLogo size={15} weight="fill" className="text-green-500 shrink-0" />
                                ) : (
                                  <Robot size={15} weight="fill" className="text-sky-500 shrink-0" />
                                )}
                                <span className="font-medium truncate max-w-[120px]">{p.channel_name}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2.5">
                              <p className="font-medium text-foreground truncate max-w-[140px]">{p.client_name}</p>
                              <p className="text-[10px] text-muted-foreground font-mono">{p.client_id}</p>
                            </td>
                            <td className="px-3 py-2.5 max-w-[280px]">
                              <p className="truncate text-foreground font-normal">{p.incoming_text || '...'}</p>
                              {p.incoming_count > 1 && (
                                <p className="text-[10px] text-muted-foreground mt-0.5">
                                  {p.incoming_count} incoming messages queued
                                </p>
                              )}
                            </td>
                            <td className="px-3 py-2.5">
                              <span className="text-muted-foreground">
                                {p.incoming_time ? new Date(p.incoming_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : 'N/A'}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              <Badge variant="outline" className="text-xs px-2.5 py-0.5 font-bold border-rose-500/30 text-rose-600 dark:text-rose-400 bg-rose-500/10">
                                ⏳ Waiting {p.waiting_formatted}
                              </Badge>
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              <Badge variant="secondary" className="text-[10px] bg-amber-500/10 text-amber-700 dark:text-amber-400">
                                Needs Reply
                              </Badge>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Sub-view 3: Staff Speed Leaderboard */}
              {delaySubTab === 'staff' && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[650px]">
                    <thead>
                      <tr className="bg-muted/30 border-b border-border text-muted-foreground">
                        <th className="text-left px-4 py-3 font-semibold">Staff Member</th>
                        <th className="text-left px-3 py-3 font-semibold">Role</th>
                        <th className="text-right px-3 py-3 font-semibold">Replies Handled</th>
                        <th className="text-center px-3 py-3 font-semibold text-amber-600 dark:text-amber-400">Average Reply Delay</th>
                        <th className="text-center px-3 py-3 font-semibold text-emerald-600 dark:text-emerald-400">Fast Rate (&lt;5m %)</th>
                        <th className="text-right px-4 py-3 font-semibold">Worst Recorded Delay</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                      {(reportData?.delayed_reports?.staff_performance || []).length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                            No staff reply activity recorded for this period.
                          </td>
                        </tr>
                      ) : (
                        (reportData?.delayed_reports?.staff_performance || []).map((s) => (
                          <tr key={s.staff_name} className="hover:bg-muted/20 transition-colors">
                            <td className="px-4 py-2.5 font-medium text-foreground">
                              👤 {s.staff_name}
                            </td>
                            <td className="px-3 py-2.5">
                              <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">
                                {s.staff_role}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-right font-medium text-foreground">
                              {s.replies_count}
                            </td>
                            <td className="px-3 py-2.5 text-center font-bold text-amber-600 dark:text-amber-400">
                              ⏱️ {s.avg_delay_formatted}
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-600 dark:text-emerald-400 bg-emerald-500/5 font-semibold">
                                {s.fast_percent}%
                              </Badge>
                            </td>
                            <td className="px-4 py-2.5 text-right text-muted-foreground">
                              {s.max_delay_formatted}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
